/**
 * MatchEvaluationRowContainer — binds one grouped match row to its view.
 *
 * Holds no state of its own: expansion is owned by the screen, because the
 * list is what knows which row is open and a molecule may not compose another
 * molecule. All this does is derive labels and bind callbacks.
 * @module features/reports/components/molecules/MatchEvaluationRow/MatchEvaluationRowContainer
 */
import React, { useCallback, useMemo } from "react";

import MatchEvaluationRowView from "./MatchEvaluationRowView";

import type { EvaluationLine } from "./MatchEvaluationRowView";

import { formatReportDate } from "@/features/scouting/utils/reportDate";
import { PlayerReportRow } from "@/features/reports/utils/mergePlayerReportRows";
import {
  type PlayerDetailMatchReportEvaluationFragment,
  ReportKind,
} from "@/generated/graphql";

/** What the row prints when a value is missing. */
const EM_DASH = "—";

/**
 * One score as the row prints it.
 * @param score - The score, or null/undefined when it was never given.
 * @returns The number, or an em dash.
 */
const toScoreLabel = (score: number | null | undefined): string =>
  score === null || score === undefined ? EM_DASH : `${score}`;

/**
 * Maps one evaluation record to the display line the View renders.
 *
 * The generated fragment stops here. The View takes primitives, so it never
 * has to know that a score is nullable or that `null` and `0` are different
 * answers — which is the Container/View boundary this repo holds, and the
 * reason a score cannot quietly render as a zero on one surface and a dash on
 * another.
 * @param evaluation - One scout's read of this player in this fixture.
 * @returns The printed form of that read.
 */
const toEvaluationLine = (
  evaluation: PlayerDetailMatchReportEvaluationFragment
): EvaluationLine => ({
  id: evaluation.evaluationId,
  scoutName: evaluation.scoutName,
  performanceLabel: toScoreLabel(evaluation.performanceScore),
  potentialLabel: toScoreLabel(evaluation.potentialScore),
  note: evaluation.note ?? null,
});

/** A row already narrowed to the match kind. */
export type MatchReportRow = Extract<
  PlayerReportRow,
  { kind: ReportKind.Match }
>;

/** Props for MatchEvaluationRowContainer. */
export interface MatchEvaluationRowContainerProps {
  /** The fixture row, grouped and owner-resolved by `mergePlayerReportRows`. */
  readonly row: MatchReportRow;
  /** Whether it is expanded. Owned by the screen. */
  readonly isExpanded: boolean;
  /** Expands or collapses, by match id. */
  readonly onToggleRow: (matchId: string) => void;
  /** Opens the full match report. */
  readonly onOpenReport: (reportId: string) => void;
  /** Test identity. */
  readonly testID?: string;
  /** Test identity for the labelled Perf/Pot pair. */
  readonly scoresTestID?: string;
}

/**
 * Prepares one match-report fixture row for rendering.
 * @param root0 - Component props
 * @param root0.row - The grouped fixture row
 * @param root0.isExpanded - Whether the evaluation is showing
 * @param root0.onToggleRow - Expands or collapses, by match id
 * @param root0.onOpenReport - Opens the full match report
 * @param root0.testID - Test identity
 * @param root0.scoresTestID - Test identity for the score pair
 * @returns The bound row element.
 */
const MatchEvaluationRowContainer = ({
  row,
  isExpanded,
  onToggleRow,
  onOpenReport,
  testID,
  scoresTestID,
}: MatchEvaluationRowContainerProps) => {
  const { owner, others, matchId } = row;

  const ownerLine = useMemo(() => toEvaluationLine(owner), [owner]);
  const otherLines = useMemo(() => others.map(toEvaluationLine), [others]);

  // Decided here so the View never inspects a nullable score. Both absent
  // is the scouting fact 'noted but never rated', which the row says in
  // words rather than as two em dashes that read as a failed load.
  const isUnscored =
    (owner.performanceScore === null || owner.performanceScore === undefined) &&
    (owner.potentialScore === null || owner.potentialScore === undefined);

  // Bound here rather than inline in JSX: an arrow created during render is a
  // new prop identity every time, which defeats the view's memo.
  const handleToggle = useCallback(() => {
    onToggleRow(matchId);
  }, [onToggleRow, matchId]);

  const handleOpenReport = useCallback(() => {
    onOpenReport(owner.reportId);
  }, [onOpenReport, owner.reportId]);

  const title = useMemo(() => {
    const home = owner.team ?? EM_DASH;
    const away = owner.opponent ?? EM_DASH;

    return `${home} vs ${away}`;
  }, [owner.team, owner.opponent]);

  const metaParts = useMemo(
    () => [
      // The kind reads as words in the meta line rather than as a chip, which
      // is how the scouting Reports list already labels the same distinction
      // and what the design draws.
      "Match report",
      // The date the merge already CHOSE for this row, not a second
      // derivation of it. `matchDate ?? reportDate` looks equivalent and is
      // not: `??` rejects only nullish, while the sort rejects anything
      // `toDateKey` cannot parse — so a fixture dated `2026-02-30` ordered by
      // its report date and printed an em dash.
      formatReportDate(row.displayDate, EM_DASH),
      owner.scoutName,
    ],
    [row.displayDate, owner.scoutName]
  );

  return (
    <MatchEvaluationRowView
      title={title}
      metaParts={metaParts}
      owner={ownerLine}
      others={otherLines}
      isUnscored={isUnscored}
      isExpanded={isExpanded}
      onToggle={handleToggle}
      onOpenReport={handleOpenReport}
      testID={testID}
      scoresTestID={scoresTestID}
    />
  );
};

MatchEvaluationRowContainer.displayName = "MatchEvaluationRowContainer";

export default React.memo(MatchEvaluationRowContainer);
